import { useMemo, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomTypography } from "sipli-fleet-core/components";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import { CROSS_ICON, SEARCH_ICON } from "src/assets/svgs";
import { useDebounce } from "src/hooks/useDebounce";
import { useQueryGetUsersList } from "src/queries/users.queries";
import AddEditUserModal from "src/container/Users/AddEditUserModal/AddEditUserModal";
import UsersTable from "src/container/Users/UsersTable/UsersTable";
import type { User } from "src/services/userService";
import CustomerLimitGuardAlert from "../CustomerLimitGuardAlert";
import CustomerLimitGuardedAction from "../CustomerLimitGuardedAction";
import { useCustomerLimitGuard } from "../useCustomerLimitGuard";
// test

const SearchIcon = SEARCH_ICON as unknown as ComponentType<{ className?: string }>;
const CrossIcon = CROSS_ICON as unknown as ComponentType<{ className?: string }>;

interface UsersTabProps {
  clientId?: string;
}

interface UsersSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

const UsersTab = ({ clientId }: UsersTabProps) => {
  const { t } = useTranslation();

  const [pagination, setPagination] = useState({ page: 0, size: 10 });
  const [sorting, setSorting] = useState<UsersSorting>({
    sortBy: "ID",
    sortDir: "DESC",
  });
  const [search, setSearch] = useState("");
  const [userModal, setUserModal] = useState<{
    open: boolean;
    record: User | null;
  }>({ open: false, record: null });
  const debouncedSearch = useDebounce(search, 500);

  const queryParams = useMemo(
    () => ({
      size: pagination.size,
      page: pagination.page,
      sortBy: sorting.sortBy,
      sort: sorting.sortDir,
      status: "ALL",
      ...(debouncedSearch.trim() ? { query: debouncedSearch.trim() } : {}),
      ...(clientId ? { clientId } : {}),
    }),
    [clientId, debouncedSearch, pagination.page, pagination.size, sorting.sortBy, sorting.sortDir]
  );

  const { data, isLoading, refetch } = useQueryGetUsersList(queryParams, {
    enabled: Boolean(clientId),
  });

  const response = data?.data?.response;
  const users: User[] = response?.users ?? [];
  const totalUsers = response?.totalItems ?? users.length;
  const usersLimitGuard = useCustomerLimitGuard({
    limitKey: "users",
    currentValue: totalUsers,
    customerId: clientId,
    enabled: Boolean(clientId),
  });

  return (
    <div className="bg-white rounded-[16px]">
      <div className="flex items-center justify-between gap-4 px-5 pt-3">
        <CustomTypography.CustomText className="!text-[17.72px] !font-bold">
          {t("users")}
        </CustomTypography.CustomText>
      </div>

      <div className="flex flex-col gap-4 px-5 my-6">
        <div className="mt-[-15px]">
          <CustomerLimitGuardAlert limitGuard={usersLimitGuard} />
        </div>
        <div className="flex items-center justify-between gap-4">
          <CustomSharedInput
            allowClear={{
              clearIcon: (
                <CrossIcon className="mr-0.5 customers-search-clear-icon w-[8.5px] h-[8.5px]" />
              ),
            }}
            prefix={<SearchIcon className="customers-search-icon w-[14px] h-[14px]" />}
            placeholder={t("search")}
            value={search}
            className="global-filter-search-class !mt-0 !rounded-[4px]"
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((previous) => ({ ...previous, page: 0 }));
            }}
          />

          <CustomerLimitGuardedAction limitGuard={usersLimitGuard}>
            <CustomButton
              type="primary"
              label={t("add_user")}
              buttonSize="medium"
              className="customer-detail-install-btn !text-white"
              onClick={() => setUserModal({ open: true, record: null })}
            />
          </CustomerLimitGuardedAction>
        </div>
      </div>

      <UsersTable
        data={users}
        isLoading={isLoading}
        pagination={{
          ...pagination,
          total: response?.totalItems ?? users.length,
        }}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        search={search}
        setSearch={setSearch}
        selectedClientId={undefined}
        setSelectedClientId={() => undefined}
        clientOptions={[]}
        isClientLookupLoading={false}
        refetchList={refetch}
        onEditUser={(record) => {
          setUserModal({ open: true, record });
        }}
        hideFilters
        showCustomerColumn={false}
      />

      <AddEditUserModal
        open={userModal.open}
        record={userModal.record}
        onClose={() => {
          setUserModal({ open: false, record: null });
          void refetch();
        }}
      />
    </div>
  );
};

export default UsersTab;
